import React, { useState, useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { LogoIcon } from '../appearence/icons/logo_icon';
import { ERROR_TYPE_LOGIN, ERROR_TYPE_PASSWORD} from "../constant";
import { CHANGE_PASSWORD_REQUEST, LOGIN_REQUEST, postFetchRequest } from '../store/requests';
import { useTranslation } from 'react-i18next';
import { LoginLayout, TextInput, PasswordInput } from '../../shared/login';
import { ArrowIcon } from '../appearence/icons/arrow_icon';
import CommitInfo from '../shared/CommitInfo';
import { keyDownService } from '../services/keyDownService';
import {makeUrl} from "../util";
import { getSafeNextPath, useAuth } from '../auth/AuthContext';
import { Button } from '@chapche/ui';

const LoginPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, loading, refresh } = useAuth();
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [forgetPasswordFormIsOpen, setForgetPasswordFormIsOpen] = useState(false);
    const [error, setError] = useState({message:'', type:''});
    const [loginButtonIsUnActive, setLoginButtonIsUnActive] = useState(false);
    const { i18n } = useTranslation();
    const nextPath = useMemo(
        () => getSafeNextPath(searchParams, '/profile'),
        [searchParams],
    );

    useEffect(() => {
        setError({message:'', type:''});
    }, [login, password, forgetPasswordFormIsOpen]);

    const successLoginCallback = async (response) => {
        if(response.status === 200) {
            await refresh();
            navigate(nextPath, { replace: true });
            setLoginButtonIsUnActive(false);
        } else if (response.status === 300) {
            await refresh();
            const result = await response.json();
            window.location.replace(result.redirect);
        } else {
            setError({type:ERROR_TYPE_PASSWORD, message:i18n.t("public.app.page.login.signInError")});
            setLoginButtonIsUnActive(false);
        }
    };

    const successChangePasswordCallback = (response) => {
        if(response.status === 200) {
            setLogin('');
            setForgetPasswordFormIsOpen(false);
            setLoginButtonIsUnActive(false);
        } else {
            setError({type:ERROR_TYPE_LOGIN, message:i18n.t("public.app.page.login.changePasswordError")});
            setLoginButtonIsUnActive(false);
        }
    };

    const errorCallback = () => {
        setError({type:ERROR_TYPE_PASSWORD, message:i18n.t("public.app.page.login.signInError")});
        setLoginButtonIsUnActive(false);
    };

    const handleLogin = () => {
        setLoginButtonIsUnActive(true);
        setPassword('');
        if(forgetPasswordFormIsOpen) {
            const data = {
                email: login,
            };
            postFetchRequest(makeUrl([CHANGE_PASSWORD_REQUEST]), data, successChangePasswordCallback, errorCallback, true);
        } else {
            if(login && password) {
                const data = {login, password};
                postFetchRequest(makeUrl([LOGIN_REQUEST]), data, successLoginCallback, errorCallback, true);
            } else {
                setLoginButtonIsUnActive(false);
            }
        }
    };

    if (!loading && user) {
        return <Navigate to={nextPath} replace />;
    }

    return (
        <LoginLayout
            logo={<LogoIcon />}
            meta={<CommitInfo />}
            onKeyDown={(e) => keyDownService.saveNewItem(e, handleLogin)}
            beforeForm={forgetPasswordFormIsOpen
                ? <ArrowIcon nameOfClass="login__icon-back" handleClick={setForgetPasswordFormIsOpen} value={false} />
                : null}
            title={
                <h1 className="login__title block__title-h3">
                    {!forgetPasswordFormIsOpen
                        ? i18n.t('public.app.page.login.title')
                        : i18n.t('public.app.page.login.enterEmail')}
                </h1>
            }
            subtitle={forgetPasswordFormIsOpen
                ? <h2 className="block__content login__subtitle">{i18n.t('public.app.page.login.content')}</h2>
                : null}
        >
            <form className="login__form" autoComplete="off">
                <TextInput
                    label={i18n.t('public.app.page.login.email')}
                    value={login}
                    onChange={(e) => setLogin(e.target.value.trim())}
                    type="email"
                />
                {!forgetPasswordFormIsOpen
                    ? <PasswordInput
                        label={i18n.t('public.app.page.login.password')}
                        password={password}
                        onChange={(e) => setPassword(e.target.value)}
                        hasError={error.type === 'password'}
                        showLabel={i18n.t('public.app.page.login.show')}
                        hideLabel={i18n.t('public.app.page.login.hide')}
                        capsLockLabel={i18n.t('public.app.page.login.capsLock')}
                    />
                    : null}
                {error.type ? <p className="block__bullet alert">{error.message}</p> : ''}
                <Button
                    type="button"
                    className="login__button"
                    disabled={loginButtonIsUnActive || !((login && password) || (login && forgetPasswordFormIsOpen))}
                    onClick={() => (loginButtonIsUnActive ? null : handleLogin())}
                >
                    {i18n.t('public.app.page.login.continue')}
                </Button>
                {!forgetPasswordFormIsOpen
                    ? <p className="block__content login__text" onClick={() => setForgetPasswordFormIsOpen(true)}>
                        {i18n.t('public.app.page.login.forgetPassword')}
                    </p>
                    : null}
            </form>
        </LoginLayout>
    );
};

export default LoginPage;
